/* ============================================
   AUDIO CONTEXT
   The Creative Orchestra - GEMINI Framework
   Manages ambient music playback across the app
   ============================================ */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Howl } from 'howler';
import { getStorageItem, setStorageItem } from '@utils/helpers';
import { STORAGE_KEYS, AUDIO_CONFIG } from '@utils/constants';

// Create context
const AudioContext = createContext(null);

/**
 * Audio Provider Component
 * Manages ambient orchestra music with play/pause, mute, and volume controls
 */
export const AudioProvider = ({ children }) => {
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(() => getStorageItem(STORAGE_KEYS.audioMuted, true)); // Start muted by default
  const [volume, setVolume] = useState(() => getStorageItem(STORAGE_KEYS.audioVolume, AUDIO_CONFIG.defaultVolume));
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Refs
  const soundRef = useRef(null);
  const fadeTimeoutRef = useRef(null);

  /**
   * Initialize Howl instance
   */
  useEffect(() => {
    // Create Howl instance
    soundRef.current = new Howl({
      src: [AUDIO_CONFIG.ambientTrack],
      loop: true,
      volume: isMuted ? 0 : volume,
      preload: true,
      html5: true, // Use HTML5 Audio for better streaming on mobile
      onload: () => {
        setIsLoaded(true);
        console.log('🎵 Ambient audio loaded');
      },
      onloaderror: (id, error) => {
        console.warn('🎵 Audio load error:', error);
        setIsLoaded(false);
      },
      onplayerror: (id, error) => {
        console.warn('🎵 Audio play error:', error);
        // Attempt to recover
        soundRef.current?.once('unlock', () => {
          soundRef.current?.play();
        });
      },
    });

    // Cleanup on unmount
    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
      if (soundRef.current) {
        soundRef.current.unload();
      }
    };
  }, []);

  /**
   * Handle user interaction to enable audio
   * Browsers require user interaction before playing audio
   */
  const handleUserInteraction = useCallback(() => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  }, [hasInteracted]);

  /**
   * Play audio with fade in
   */
  const play = useCallback(() => {
    if (!soundRef.current || !isLoaded) return;

    const sound = soundRef.current;
    
    if (!sound.playing()) {
      // Start at 0 volume for fade in
      sound.volume(0);
      sound.play();
      
      // Fade in
      sound.fade(0, isMuted ? 0 : volume, AUDIO_CONFIG.fadeInDuration);
    }
    
    setIsPlaying(true);
  }, [isLoaded, isMuted, volume]);

  /**
   * Pause audio with fade out
   */
  const pause = useCallback(() => {
    if (!soundRef.current) return;

    const sound = soundRef.current;
    
    if (sound.playing()) {
      // Fade out then pause
      sound.fade(sound.volume(), 0, AUDIO_CONFIG.fadeOutDuration);
      
      fadeTimeoutRef.current = setTimeout(() => {
        sound.pause();
      }, AUDIO_CONFIG.fadeOutDuration);
    }
    
    setIsPlaying(false);
  }, []);

  /**
   * Toggle play/pause
   */
  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  /**
   * Toggle mute
   */
  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    setStorageItem(STORAGE_KEYS.audioMuted, newMuted);

    if (soundRef.current) {
      if (newMuted) {
        soundRef.current.fade(soundRef.current.volume(), 0, 300);
      } else {
        soundRef.current.fade(0, volume, 300);
      }
    }
  }, [isMuted, volume]);

  /**
   * Set volume level
   */
  const setVolumeLevel = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    setStorageItem(STORAGE_KEYS.audioVolume, clampedVolume);

    if (soundRef.current && !isMuted) {
      soundRef.current.volume(clampedVolume);
    }
  }, [isMuted]);

  /**
   * Increase volume
   */
  const increaseVolume = useCallback(() => {
    setVolumeLevel(volume + 0.1);
  }, [volume, setVolumeLevel]);

  /**
   * Decrease volume
   */
  const decreaseVolume = useCallback(() => {
    setVolumeLevel(volume - 0.1);
  }, [volume, setVolumeLevel]);

  /**
   * Auto-play when user has interacted and audio is loaded
   * Only if not muted
   */
  useEffect(() => {
    if (hasInteracted && isLoaded && !isMuted && !isPlaying) {
      // Small delay to ensure smooth experience
      const timer = setTimeout(() => {
        play();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasInteracted, isLoaded, isMuted, isPlaying, play]);

  /**
   * Add global click listener for user interaction detection
   */
  useEffect(() => {
    const handleClick = () => {
      handleUserInteraction();
      // Remove listener after first interaction
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleClick);
    document.addEventListener('touchstart', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [handleUserInteraction]);

  // Context value
  const value = {
    // State
    isPlaying,
    isMuted,
    volume,
    isLoaded,
    hasInteracted,
    
    // Actions
    play,
    pause,
    togglePlay,
    toggleMute,
    setVolumeLevel,
    increaseVolume,
    decreaseVolume,
    handleUserInteraction,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

/**
 * Custom hook to use audio context
 * @returns {Object} Audio context value
 */
export const useAudio = () => {
  const context = useContext(AudioContext);
  
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  
  return context;
};

export default AudioContext;
